import { PostDto, UpdatePostDto } from "@growth/dto";
import { isDocEmpty, slugify, slugSchema, TiptapDoc } from "@growth/schema";
import { Badge, Button, ConfirmDialog, Field, Input, Textarea } from "@growth/ui";
import { useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  CircleAlert,
  CircleCheck,
  CloudUpload,
  ExternalLink,
  LoaderCircle,
  Trash2,
} from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { RichTextEditor } from "../components/editor/rich-text-editor";
import { FullPageMessage } from "../components/full-page-message";
import { ImageField } from "../components/image-field";
import { LinkPreview } from "../components/link-preview";
import { SocialFromArticle } from "../components/social-from-article";
import { TagsInput } from "../components/tags-input";
import { type SaveStatus, usePendingChanges } from "../hooks/use-pending-changes";
import { useActive, useSwitchActive } from "../lib/active";
import { generateImage, useAiStatus } from "../lib/ai";
import { apiErrorMessage, apiErrorStatus } from "../lib/api-error";
import { useAuthors } from "../lib/authors";
import { formatDate, formatRelative } from "../lib/format";
import { postKeys, updatePost, useDeletePost, usePost, usePostAction } from "../lib/posts";
import { useSite } from "../lib/workspace";

export const PostEditorPage = () => {
  const { id = "" } = useParams();
  const post = usePost(id);
  const { workspace, brand } = useActive();
  const switchActive = useSwitchActive();

  // A post opened from a link may belong to another brand: work on that brand.
  const owner = workspace?.brands.find((candidate) => candidate.siteId === post.data?.siteId);
  useEffect(() => {
    if (workspace && owner && owner.id !== brand?.id) void switchActive(workspace.id, owner.id);
  }, [workspace, owner, brand?.id, switchActive]);

  if (post.isPending) {
    return (
      <div className="flex justify-center py-24" aria-label="Loading post">
        <LoaderCircle className="size-6 animate-spin text-muted" />
      </div>
    );
  }
  if (post.isError) {
    return (
      <FullPageMessage
        icon={<CircleAlert className="size-5" />}
        title={apiErrorStatus(post.error) === 404 ? "Post not found" : "Couldn't load the post"}
      >
        <p>
          {apiErrorStatus(post.error) === 404
            ? "It may have been deleted."
            : apiErrorMessage(post.error)}
        </p>
        <Button asChild variant="secondary">
          <Link to="/">Back to posts</Link>
        </Button>
      </FullPageMessage>
    );
  }
  return <PostEditor key={post.data.id} initial={post.data} />;
};

type Form = Required<Omit<UpdatePostDto, "body">>;

const toForm = (post: PostDto): Form => ({
  title: post.title,
  slug: post.slug,
  excerpt: post.excerpt ?? "",
  coverImageUrl: post.coverImageUrl,
  tags: post.tags,
  authorId: post.authorId,
  seoTitle: post.seoTitle ?? "",
  seoDescription: post.seoDescription ?? "",
  ogImageUrl: post.ogImageUrl,
});

/** Empty optional text fields are stored as null. */
const toPatchValue = (key: keyof Form, value: Form[keyof Form]) =>
  ["excerpt", "seoTitle", "seoDescription"].includes(key) && value === "" ? null : value;

const PostEditor = ({ initial }: { initial: PostDto }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { brand } = useActive();
  const site = useSite().data;
  // Subscribed, so publish/unpublish/saves re-render with the server's latest copy.
  const post = usePost(initial.id).data ?? initial;
  const isPublished = post.status === "PUBLISHED";

  const [form, setForm] = useState<Form>(() => toForm(initial));
  const [slugTouched, setSlugTouched] = useState(
    () => initial.slug !== (slugify(initial.title) || "untitled"),
  );
  const [slugError, setSlugError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const body = useRef<TiptapDoc>(initial.body);

  const authors = useAuthors();
  const aiStatus = useAiStatus();
  const aiEnabled = Boolean(aiStatus.data?.enabled);
  const action = usePostAction(post.id);
  const deletePost = useDeletePost();

  const saver = usePendingChanges<UpdatePostDto>(
    async (changes) => {
      const store = (updated: PostDto) =>
        queryClient.setQueryData(postKeys.detail(post.id), updated);
      try {
        store(await updatePost(post.id, changes));
        if (changes.slug) setSlugError(null);
      } catch (error) {
        // A taken slug must not block saving everything else: report it, save the rest.
        if (apiErrorStatus(error) === 409 && changes.slug) {
          setSlugError(apiErrorMessage(error));
          const { slug: _slug, ...rest } = changes;
          if (Object.keys(rest).length > 0) store(await updatePost(post.id, rest));
          return;
        }
        throw error;
      }
      void queryClient.invalidateQueries({ queryKey: ["posts", "list"] });
    },
    { delay: 1200, autoSave: !isPublished },
  );

  // Warn before leaving with unsaved edits.
  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (saver.hasPending() || saver.status === "saving") event.preventDefault();
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [saver.status]);

  const set = <K extends keyof Form>(key: K, value: Form[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
    saver.queue({ [key]: toPatchValue(key, value) } as UpdatePostDto);
  };

  const setTitle = (title: string) => {
    if (slugTouched || isPublished) return set("title", title);
    const slug = slugify(title) || "untitled";
    setForm((current) => ({ ...current, title, slug }));
    saver.queue({ title, slug });
  };

  const setSlug = (raw: string) => {
    setSlugTouched(true);
    const slug = raw.toLowerCase().replace(/\s+/g, "-");
    setForm((current) => ({ ...current, slug }));
    const valid = slugSchema.safeParse(slug);
    if (!valid.success) {
      setSlugError(valid.error.issues[0]?.message ?? "Invalid slug");
      saver.discard(["slug"]);
      return;
    }
    setSlugError(null);
    saver.queue({ slug });
  };

  const run = async (kind: "publish" | "unpublish" | "update") => {
    setActionError(null);
    if (kind !== "unpublish" && (!form.title.trim() || isDocEmpty(body.current))) {
      setActionError("Add a title and some body content first.");
      return;
    }
    if (!(await saver.flush())) {
      setActionError(apiErrorMessage(saver.error, "Couldn't save your changes"));
      return;
    }
    if (kind === "update") return;
    action.mutate(kind, {
      onError: (error) => setActionError(apiErrorMessage(error)),
    });
  };

  const remove = () =>
    deletePost.mutate(post.id, {
      onSuccess: () => navigate("/", { replace: true }),
      onError: (error) => setActionError(apiErrorMessage(error, "Couldn't delete the post")),
    });

  const domain = site?.domain ?? "";
  const busy = action.isPending || saver.status === "saving";

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex items-center justify-between gap-4">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-ink">
          <ArrowLeft className="size-4" />
          {brand?.name} posts
        </Link>
        <SaveIndicator
          status={saver.status}
          published={isPublished}
          onRetry={() => void saver.flush()}
        />
      </div>

      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="min-w-0 space-y-4">
          <textarea
            aria-label="Title"
            value={form.title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Post title"
            rows={1}
            maxLength={200}
            className="w-full resize-none overflow-hidden bg-transparent font-serif text-4xl leading-tight text-ink outline-none placeholder:text-muted/50 [field-sizing:content]"
          />
          <RichTextEditor
            theme={site?.theme ?? "firstround"}
            title={form.title}
            aiEnabled={aiEnabled}
            initialContent={initial.body}
            onError={setActionError}
            onChange={(doc) => {
              body.current = doc;
              saver.queue({ body: doc });
            }}
          />
        </div>

        <aside className="space-y-4 lg:sticky lg:top-6 lg:self-start">
          <Panel>
            <div className="flex items-center justify-between">
              {isPublished ? <Badge tone="success">Published</Badge> : <Badge>Draft</Badge>}
              {isPublished && post.publishedAt && (
                <span className="text-xs text-muted">{formatDate(post.publishedAt)}</span>
              )}
            </div>
            {isPublished ? (
              <div className="grid grid-cols-2 gap-2">
                <Button onClick={() => void run("update")} disabled={busy || !saver.hasPending()}>
                  {saver.status === "saving" ? (
                    <LoaderCircle className="size-4 animate-spin" />
                  ) : (
                    <CloudUpload className="size-4" />
                  )}
                  Update
                </Button>
                <ConfirmDialog
                  trigger={
                    <Button variant="secondary" disabled={busy}>
                      Unpublish
                    </Button>
                  }
                  title="Unpublish this post?"
                  description={`It will disappear from ${domain}/blog until you publish it again.`}
                  confirmLabel="Unpublish"
                  onConfirm={() => void run("unpublish")}
                />
              </div>
            ) : (
              <Button className="w-full" onClick={() => void run("publish")} disabled={busy}>
                {action.isPending ? (
                  <LoaderCircle className="size-4 animate-spin" />
                ) : (
                  <CloudUpload className="size-4" />
                )}
                Publish to {brand?.name}
              </Button>
            )}
            {isPublished && (
              <p className="text-xs text-muted">
                Edits to a published post go live when you click Update.
              </p>
            )}
            {actionError && (
              <p role="alert" className="flex gap-1.5 text-xs text-red-600">
                <CircleAlert className="mt-px size-3.5 shrink-0" />
                {actionError}
              </p>
            )}
            {isPublished && (
              <a
                href={`https://${domain}/blog/${post.slug}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs text-accent hover:underline"
              >
                View on {domain}
                <ExternalLink className="size-3" />
              </a>
            )}
          </Panel>

          {isPublished && <SocialFromArticle postId={post.id} />}

          <Panel title="Details">
            <Field
              label="URL slug"
              error={slugError}
              hint={
                <span className="break-all">
                  {domain}/blog/{form.slug || "…"}
                </span>
              }
            >
              {(props) => (
                <Input
                  {...props}
                  value={form.slug}
                  onChange={(event) => setSlug(event.target.value)}
                />
              )}
            </Field>
            <Field label="Author">
              {(props) => (
                <select
                  {...props}
                  value={form.authorId ?? ""}
                  onChange={(event) => set("authorId", event.target.value || null)}
                  className="h-9 w-full rounded-md border border-line bg-white px-2 text-sm text-ink focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
                >
                  <option value="">No byline</option>
                  {authors.data?.map((author) => (
                    <option key={author.id} value={author.id}>
                      {author.name}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            <Field label="Tags" hint="Press Enter or comma to add. Up to 10.">
              {({ id }) => (
                <TagsInput id={id} value={form.tags} onChange={(tags) => set("tags", tags)} />
              )}
            </Field>
            <Field
              label="Excerpt"
              hint="Shown on the blog list."
              counter={{ value: form.excerpt?.length ?? 0, max: 300 }}
            >
              {(props) => (
                <Textarea
                  {...props}
                  rows={3}
                  maxLength={300}
                  value={form.excerpt ?? ""}
                  onChange={(event) => set("excerpt", event.target.value)}
                />
              )}
            </Field>
            <ImageField
              label="Cover image"
              value={form.coverImageUrl}
              onChange={(url) => set("coverImageUrl", url)}
              onGenerate={
                aiEnabled
                  ? async () =>
                      (
                        await generateImage({
                          purpose: "cover",
                          context:
                            [form.title, form.excerpt].filter(Boolean).join("\n") || "Blog cover",
                        })
                      ).url
                  : undefined
              }
            />
          </Panel>

          <Panel title="Search & sharing">
            <Field
              label="SEO title"
              hint="Defaults to the post title."
              counter={{ value: (form.seoTitle || form.title).length, max: 60 }}
            >
              {(props) => (
                <Input
                  {...props}
                  maxLength={120}
                  value={form.seoTitle ?? ""}
                  placeholder={form.title}
                  onChange={(event) => set("seoTitle", event.target.value)}
                />
              )}
            </Field>
            <Field
              label="Meta description"
              hint="Defaults to the excerpt."
              counter={{ value: (form.seoDescription || form.excerpt || "").length, max: 160 }}
            >
              {(props) => (
                <Textarea
                  {...props}
                  rows={3}
                  maxLength={300}
                  value={form.seoDescription ?? ""}
                  placeholder={form.excerpt ?? ""}
                  onChange={(event) => set("seoDescription", event.target.value)}
                />
              )}
            </Field>
            <ImageField
              label="Social image"
              hint="1200 × 630. Defaults to the cover image."
              aspect="1200 / 630"
              value={form.ogImageUrl}
              onChange={(url) => set("ogImageUrl", url)}
            />
            <LinkPreview
              domain={domain}
              title={form.seoTitle || form.title}
              description={form.seoDescription || form.excerpt || ""}
              image={form.ogImageUrl ?? form.coverImageUrl}
            />
          </Panel>

          <ConfirmDialog
            trigger={
              <Button
                variant="ghost"
                className="w-full text-red-600 hover:bg-red-50 hover:text-red-700"
                disabled={deletePost.isPending}
              >
                <Trash2 className="size-4" />
                Delete post
              </Button>
            }
            title="Delete this post?"
            description={
              isPublished
                ? `It is live on ${domain} and will be removed immediately. This can't be undone.`
                : "This can't be undone."
            }
            confirmLabel="Delete"
            destructive
            onConfirm={remove}
          />
          <p className="text-center text-[11px] text-muted">
            Last saved {formatRelative(post.updatedAt)}
          </p>
        </aside>
      </div>
    </div>
  );
};

const Panel = ({ title, children }: { title?: string; children: ReactNode }) => (
  <section className="space-y-4 rounded-xl border border-line bg-white p-4">
    {title && (
      <h2 className="text-xs font-semibold uppercase tracking-widest text-muted">{title}</h2>
    )}
    {children}
  </section>
);

const SaveIndicator = ({
  status,
  published,
  onRetry,
}: {
  status: SaveStatus;
  published: boolean;
  onRetry: () => void;
}) => {
  if (status === "saving") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-muted" role="status">
        <LoaderCircle className="size-3.5 animate-spin" />
        Saving…
      </span>
    );
  }
  if (status === "error") {
    return (
      <span className="inline-flex items-center gap-2 text-xs text-red-600" role="status">
        <CircleAlert className="size-3.5" />
        Couldn't save
        <button type="button" onClick={onRetry} className="font-medium underline">
          Retry
        </button>
      </span>
    );
  }
  if (status === "pending") {
    return (
      <span className="text-xs text-muted" role="status">
        {published ? "Changes not live yet" : "Unsaved changes"}
      </span>
    );
  }
  if (status === "saved") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-muted" role="status">
        <CircleCheck className="size-3.5 text-emerald-600" />
        {published ? "Live" : "Saved"}
      </span>
    );
  }
  return null;
};
